/*
 * Shown when the user selects a WASTE container for one or more donation-eligible items.
 */

import React, { useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import Select, { SelectOption } from "@amzn/meridian/select";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import alertKnockoutTokens from "@amzn/meridian-tokens/base/icon/alert-knockout";
import { useLocation, useNavigate } from "react-router-dom";
import { WadmContainer, ContainerUsingFor, ItemFlowContext } from "src/types/containerTypes";
import PillButton from "src/components/UI/PillButton";
import { DONATION_OVERRIDE_REASONS } from "src/constants/constants";
import "src/components/pages/classification/classification.scss";
import "./toteItems.scss";

interface DonationOverrideLocationState {
  itemFlowContext: ItemFlowContext;
  container: WadmContainer;
  usingFor: ContainerUsingFor;
}

const DonationOverridePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as DonationOverrideLocationState | undefined;

  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [bulkReason, setBulkReason] = useState("");

  if (!state) {
    return (
      <Column height="100vh" alignmentHorizontal="center" alignmentVertical="center" spacing="400">
        <Text type="h200">No data found</Text>
        <PillButton text="Go Home" color="secondary" onClick={() => navigate("/home")} />
      </Column>
    );
  }

  const { itemFlowContext, container, usingFor } = state;

  const getEligibleItems = () => {
    if (itemFlowContext.type === "tote") {
      return itemFlowContext.items
        .filter((item) => item.donationEligibilityInformation?.isEligibleForDonation === true)
        .map((item) => ({
          id: item.id,
          name: item.name,
          asin: item.asin,
          imageUrl: item.images?.[0]?.url,
        }));
    }

    if (itemFlowContext.type === "classification" || itemFlowContext.type === "toteExtraItem") {
      const pc = itemFlowContext.productClassification;
      const isEligible =
        pc?.eligible_for_donation === true ||
        pc?.donation_eligibility_information?.isEligibleForDonation === true;
      if (isEligible) {
        return [
          { id: pc.asin, name: pc.name ?? pc.asin, asin: pc.asin, imageUrl: pc.images?.[0]?.url },
        ];
      }
    }
    return [];
  };

  const eligibleItems = getEligibleItems();

  const allReasonsSelected =
    eligibleItems.length > 0 && eligibleItems.every((item) => reasons[item.id] !== undefined);

  const handleApplyToAll = (value: string) => {
    setBulkReason(value);
    setReasons(Object.fromEntries(eligibleItems.map((item) => [item.id, value])));
  };

  const handleConfirm = () => {
    navigate("/tote/confirmQuantity", {
      state: {
        itemFlowContext,
        container,
        usingFor,
        overrideReasons: reasons,
      },
    });
  };

  return (
    <Column className="app-gradient-bg pwa-scrollable" spacing="400" spacingInset="none none 600">
      <Row alignmentVertical="center" spacingInset="500 400 none" spacing="none">
        <Button type="icon" onClick={() => navigate(-1)}>
          <Icon color="success" tokens={arrowLeftTokens} />
        </Button>
        <Column spacing="none" spacingInset="200">
          <Text type="h100" color="success">
            Waste reason required
          </Text>
          <Text type="b100" color="secondary">
            These items are eligible for donation
          </Text>
        </Column>
      </Row>

      <Column spacingInset="none 500" spacing="400">
        <div className="donation-override-warning" style={{ padding: "16px" }}>
          <Row spacing="300" alignmentVertical="top">
            <Icon tokens={alertKnockoutTokens} />
            <Text type="b200">
              These items are eligible for donation. Please select a reason for sending each to a
              waste container.
            </Text>
          </Row>
        </div>

        {eligibleItems.length > 1 && (
          <Column spacingInset="300 400" spacing="300" className="home-card">
            <Text type="b300">Apply to all items</Text>
            <Select
              value={bulkReason}
              onChange={handleApplyToAll}
              placeholder="Select waste reason"
              size="small"
            >
              {DONATION_OVERRIDE_REASONS.map((r) => (
                <SelectOption key={r.value} value={r.value} label={r.label} />
              ))}
            </Select>
          </Column>
        )}

        {eligibleItems.map((item) => (
          <Column key={item.id} spacing="200" className="home-card">
            <Column spacingInset="300 400" spacing="300">
              <Row spacing="400" alignmentVertical="center">
                {item.imageUrl && (
                  <div className="tote-item-image-box">
                    <img
                      src={item.imageUrl}
                      alt={item.name ?? "Product"}
                      className="tote-item-image"
                    />
                  </div>
                )}
                <Column spacing="100" width="fill">
                  <Text type="b300">{item.name ?? "Unknown Product"}</Text>
                  {item.asin && (
                    <Text type="b200" color="secondary">
                      ASIN: {item.asin}
                    </Text>
                  )}
                </Column>
              </Row>
              <Select
                value={reasons[item.id] ?? ""}
                onChange={(value: string) => setReasons((prev) => ({ ...prev, [item.id]: value }))}
                placeholder="Select waste reason"
                size="small"
              >
                {DONATION_OVERRIDE_REASONS.map((r) => (
                  <SelectOption key={r.value} value={r.value} label={r.label} />
                ))}
              </Select>
            </Column>
          </Column>
        ))}
      </Column>

      <Column className="asin-sticky-footer" spacing="300">
        <PillButton
          text="CONTINUE"
          color="success"
          onClick={handleConfirm}
          disabled={!allReasonsSelected}
        />
      </Column>
    </Column>
  );
};

export default DonationOverridePage;
